import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Award, Flame } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BadgeCard = ({ badge, testId }) => {
  const pct = Math.min(100, Math.round((badge.progress / (badge.target || 1)) * 100));
  return (
    <div
      className={`rounded-xl p-5 border-2 transition-all ${
        badge.earned
          ? 'bg-gradient-to-br from-yellow-50 to-amber-100 border-amber-300 shadow-md'
          : 'bg-white border-gray-200 opacity-80'
      }`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-14 h-14 rounded-full flex items-center justify-center text-3xl ${
          badge.earned ? 'bg-amber-200' : 'bg-gray-100 grayscale'
        }`}>
          {badge.icon}
        </div>
        {badge.earned ? (
          <span className="text-[10px] font-bold uppercase bg-emerald-500 text-white px-2 py-1 rounded-full">Earned</span>
        ) : (
          <span className="text-[10px] font-bold uppercase bg-gray-200 text-gray-600 px-2 py-1 rounded-full">Locked</span>
        )}
      </div>
      <h4 className="font-bold text-gray-800">{badge.name}</h4>
      <p className="text-xs text-gray-500 mt-1 mb-3">{badge.desc}</p>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all ${badge.earned ? 'bg-emerald-500' : 'bg-amber-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-[11px] text-gray-500 mt-1 text-right">
        {badge.progress}/{badge.target}
      </p>
    </div>
  );
};

const StudentBadges = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/student/badges`);
        setData(res.data);
      } catch (e) {
        console.error('badges load error', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 flex items-center justify-center" data-testid="badges-loading">
        <Loader2 className="animate-spin text-yellow-500" size={22} />
      </div>
    );
  }

  const badges = data?.badges || [];
  const earnedCount = data?.earned_count || 0;
  const total = data?.total || badges.length;
  const streak = data?.current_streak || 0;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="student-badges">
      <div className="bg-gradient-to-r from-yellow-500 to-amber-600 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <Award size={22} />
          <div>
            <h3 className="font-semibold text-lg">My Awards & Badges</h3>
            <p className="text-yellow-100 text-xs">Earned {earnedCount} of {total} badges</p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1.5 text-white">
          <Flame size={18} className="text-orange-200" />
          <span className="font-bold text-sm">{streak}-day streak</span>
        </div>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {badges.map((b) => (
          <BadgeCard key={b.id} badge={b} testId={`badge-${b.id}`} />
        ))}
      </div>
    </div>
  );
};

export default StudentBadges;
